/** Parse unified diff text (one file) into hunks. */
export function parseUnified(text) {
    const hunks = [];
    let cur;
    for (const line of text.split('\n')) {
        const m = line.match(/^@@ -(\d+)(?:,(\d+))? \+(\d+)(?:,(\d+))? @@/);
        if (m) {
            cur = {
                oldStart: Number(m[1]),
                oldLines: m[2] == null ? 1 : Number(m[2]),
                newStart: Number(m[3]),
                newLines: m[4] == null ? 1 : Number(m[4]),
                lines: [],
            };
            hunks.push(cur);
            continue;
        }
        if (!cur)
            continue;
        if (line.startsWith('+++') || line.startsWith('---'))
            continue;
        if (/^[ +\-\\]/.test(line))
            cur.lines.push(line);
        else if (line === '')
            cur.lines.push(' ');
    }
    // Trailing empty context produced by a final newline.
    for (const h of hunks) {
        while (h.lines.length && h.lines[h.lines.length - 1] === ' ' && countOld(h) > h.oldLines)
            h.lines.pop();
    }
    return hunks;
}
function countOld(h) {
    return h.lines.filter((l) => l.startsWith(' ') || l.startsWith('-')).length;
}
export function fileHunks(f) {
    if (f.hunks?.length)
        return f.hunks;
    if (f.unified)
        return parseUnified(f.unified);
    return [];
}
export function fileStats(f) {
    if (f.additions != null || f.deletions != null)
        return { additions: f.additions ?? 0, deletions: f.deletions ?? 0 };
    let additions = 0;
    let deletions = 0;
    for (const h of fileHunks(f)) {
        for (const l of h.lines) {
            if (l.startsWith('+'))
                additions++;
            else if (l.startsWith('-'))
                deletions++;
        }
    }
    return { additions, deletions };
}
export function hunkRows(h, wordLevel = true) {
    const rows = [];
    let o = h.oldStart;
    let n = h.newStart;
    for (const raw of h.lines) {
        const tag = raw[0];
        const text = raw.slice(1);
        if (tag === '+')
            rows.push({ type: 'add', text, newNo: n++ });
        else if (tag === '-')
            rows.push({ type: 'del', text, oldNo: o++ });
        else if (tag === '\\')
            rows.push({ type: 'meta', text: raw.slice(2) });
        else
            rows.push({ type: 'ctx', text, oldNo: o++, newNo: n++ });
    }
    if (wordLevel)
        pairWords(rows);
    return {
        header: `@@ -${h.oldStart},${h.oldLines} +${h.newStart},${h.newLines} @@`,
        rows,
    };
}
/** Pair each run of deletions with the following run of additions and diff them by word. */
function pairWords(rows) {
    let i = 0;
    while (i < rows.length) {
        if (rows[i]?.type !== 'del') {
            i++;
            continue;
        }
        const dels = [];
        while (rows[i]?.type === 'del')
            dels.push(rows[i++]);
        const adds = [];
        while (rows[i]?.type === 'add')
            adds.push(rows[i++]);
        const pairs = Math.min(dels.length, adds.length);
        for (let k = 0; k < pairs; k++) {
            const d = dels[k];
            const a = adds[k];
            const wd = wordDiff(d.text, a.text);
            if (wd) {
                d.segments = wd.old;
                a.segments = wd.new;
            }
        }
    }
}
const TOKEN = /(\s+|[A-Za-z0-9_]+|[^A-Za-z0-9_\s])/g;
/**
 * Word-level LCS diff. Returns null when the lines are too different for word highlights to
 * help (then the whole line reads as changed) or too long to diff cheaply.
 */
export function wordDiff(a, b) {
    const ta = a.match(TOKEN) ?? [];
    const tb = b.match(TOKEN) ?? [];
    if (ta.length * tb.length > 40_000)
        return null;
    const m = ta.length;
    const n = tb.length;
    const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
    for (let i = m - 1; i >= 0; i--) {
        for (let j = n - 1; j >= 0; j--) {
            const row = dp[i];
            row[j] =
                ta[i] === tb[j]
                    ? dp[i + 1][j + 1] + 1
                    : Math.max(dp[i + 1][j], row[j + 1]);
        }
    }
    const oldSegs = [];
    const newSegs = [];
    const push = (list, text, changed) => {
        const last = list[list.length - 1];
        if (last && last.changed === changed)
            last.text += text;
        else
            list.push({ text, changed });
    };
    let i = 0;
    let j = 0;
    while (i < m && j < n) {
        if (ta[i] === tb[j]) {
            push(oldSegs, ta[i], false);
            push(newSegs, tb[j], false);
            i++;
            j++;
        }
        else if (dp[i + 1][j] >= dp[i][j + 1]) {
            push(oldSegs, ta[i++], true);
        }
        else {
            push(newSegs, tb[j++], true);
        }
    }
    while (i < m)
        push(oldSegs, ta[i++], true);
    while (j < n)
        push(newSegs, tb[j++], true);
    // Too little in common (by non-whitespace characters): highlight the whole line instead.
    const kept = oldSegs.filter((s) => !s.changed).reduce((n, s) => n + s.text.trim().length, 0);
    const size = Math.max(a.trim().length, b.trim().length, 1);
    if (kept / size < 0.3)
        return null;
    return { old: oldSegs, new: newSegs };
}
/** Align unified rows into side-by-side pairs. */
export function splitRows(rows) {
    const out = [];
    let i = 0;
    while (i < rows.length) {
        const r = rows[i];
        if (r.type === 'ctx' || r.type === 'meta') {
            out.push({ left: r, right: r });
            i++;
            continue;
        }
        const dels = [];
        const adds = [];
        while (rows[i]?.type === 'del')
            dels.push(rows[i++]);
        while (rows[i]?.type === 'add')
            adds.push(rows[i++]);
        const len = Math.max(dels.length, adds.length);
        for (let k = 0; k < len; k++)
            out.push({ left: dels[k], right: adds[k] });
    }
    return out;
}
export function languageFromPath(path) {
    if (!path)
        return undefined;
    const ext = path.split('.').pop()?.toLowerCase();
    const map = {
        ts: 'ts',
        tsx: 'tsx',
        js: 'js',
        jsx: 'jsx',
        mjs: 'js',
        json: 'json',
        md: 'md',
        css: 'css',
        html: 'html',
        py: 'python',
        rs: 'rust',
        go: 'go',
        swift: 'swift',
        sh: 'bash',
        yml: 'yaml',
        yaml: 'yaml',
        toml: 'toml',
        sql: 'sql',
        cs: 'csharp',
    };
    return ext ? map[ext] : undefined;
}
//# sourceMappingURL=diff.js.map